'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import styled, { css } from 'styled-components';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { useGeneratedId } from '../../hooks/useGeneratedId.js';
import '../../mixins/media.js';
import { keyboardFocusShadow } from '../../mixins/keyboardFocus.js';
import '../../mixins/markStyle.js';
import '../../mixins/scrollbarStyle.js';
import { spacing } from '../../mixins/spacing.js';
import '../../mixins/textStyle.js';
import { Avatar } from '../Avatar/Avatar.js';
import { Box } from '../Box/Box.js';
import { Clickable } from '../Clickable/Clickable.js';
import { Text } from '../Text/Text.js';
import { getPersonLineclampStyles } from './mixins/getPersonLineclampStyles.js';

const PersonWrapper = styled(Box).withConfig({
  shouldForwardProp: prop => !['orientation'].includes(prop)
}).withConfig({
  componentId: "sc-1ewf25b-0"
})(["display:flex;flex:1 0 ", ";align-items:flex-start;margin-right:", ";", ""], ({
  theme
}) => theme.components.metadata.authorWidth, ({
  theme
}) => theme.components.metadata.authorMargin, ({
  orientation
}) => orientation === 'vertical' && css(["flex-direction:column;"]));
const Info = styled('div').withConfig({
  shouldForwardProp: prop => !['orientation'].includes(prop)
}).withConfig({
  componentId: "sc-1ewf25b-1"
})(["display:flex;flex-direction:column;align-self:center;align-items:flex-start;justify-content:center;min-height:40px;width:100%;", " padding:", " 0;"], ({
  theme,
  orientation
}) => css(["", ":", ";"], orientation === 'horizontal' ? 'margin-left' : 'margin-top', theme.components.metadata.authorMargin), ({
  theme
}) => theme.components.metadata.authorInfoPadding);
const Name = styled(Text).withConfig({
  componentId: "sc-1ewf25b-2"
})(["color:", ";margin-bottom:", ";overflow:hidden;display:-webkit-box;text-align:left;-webkit-box-orient:vertical;", " ", ""], ({
  theme
}) => theme.components.metadata.authorNameFontColor, ({
  theme
}) => theme.components.metadata.authorNameMargin, ({
  lineClamp = 0,
  isExpanded
}) => !isExpanded && getPersonLineclampStyles(lineClamp), keyboardFocusShadow());
const ContactDetails = styled.div.withConfig({
  componentId: "sc-1ewf25b-3"
})(["margin-top:", ";"], spacing(2));
const PersonAvatar = styled(Avatar).withConfig({
  componentId: "sc-1ewf25b-4"
})(["flex-shrink:0;"]);
const ProfessionalDetails = styled(Text).withConfig({
  componentId: "sc-1ewf25b-5"
})(["color:", ";width:100%;"], ({
  theme
}) => theme.components.metadata.authorFunctionFontColor);
const ContactDetail = styled(Text).withConfig({
  componentId: "sc-1ewf25b-6"
})(["color:", ";& + &{margin-top:", ";}"], ({
  theme
}) => theme.components.metadata.authorNameFontColor, spacing(1));
const getInitialsFromName = name => name.split(' ').map(part => part.charAt(0)).join('');
const Person = /*#__PURE__*/withTitan(({
  avatar,
  className,
  contactDetails,
  name,
  nameLineClamp = 1,
  initials,
  orientation = 'horizontal',
  professionalDetails,
  spaceAfter,
  testId = 'person'
}) => {
  const infoRef = useRef(null);
  const nameRef = useRef(null);
  const [isButton, setIsButton] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const generatedId = useGeneratedId(undefined, 'person-contact');
  const {
    width
  } = useResizeDetector({
    targetRef: infoRef,
    handleHeight: false
  });
  useEffect(() => {
    if (!infoRef.current || !nameRef.current) {
      return;
    }
    const nameWidth = nameRef.current.getBoundingClientRect().width;
    const infoWidth = infoRef.current.getBoundingClientRect().width;
    setIsButton(nameWidth === infoWidth);
  }, [infoRef, nameRef, width]);
  const nameComponent = jsx(Name, {
    ref: nameRef,
    "aria-label": name,
    "data-test-id": `${testId}Name`,
    isExpanded: isExpanded,
    lineClamp: nameLineClamp,
    type: "labelBold",
    children: name
  });
  return jsxs(PersonWrapper, {
    className: className,
    "data-test-id": testId,
    orientation: orientation,
    spaceAfter: spaceAfter,
    children: [jsx(PersonAvatar, Object.assign({}, avatar, {
      testId: `${testId}Avatar`,
      children: !(avatar === null || avatar === void 0 ? void 0 : avatar.src) && initials || getInitialsFromName(name)
    })), jsxs(Info, {
      ref: infoRef,
      "data-test-id": `${testId}Info`,
      orientation: orientation,
      children: [isButton && !isExpanded ? jsx(Clickable, {
        "data-test-id": `${testId}NameButton`,
        tabIndex: 0,
        onClick: () => setIsExpanded(true),
        children: nameComponent
      }) : nameComponent, professionalDetails && jsx(ProfessionalDetails, {
        "data-test-id": `${testId}ProfessionalDetails`,
        type: "labelSmall",
        children: professionalDetails
      }), contactDetails && jsx(ContactDetails, {
        children: contactDetails.map((contact, i) => jsx(ContactDetail, {
          isBlock: true,
          "data-test-id": `${testId}ContactDetail${i}`,
          type: "label",
          children: contact
        }, `${generatedId}-${i}`))
      })]
    })]
  });
}, {
  name: 'Person'
});

export { Person, PersonAvatar };
//# sourceMappingURL=Person.js.map
